import { ReactNode, useCallback } from 'react';

import { BoardThemes, ChessBoard } from '@exports/ChessBoard';
import { ChessWinner } from '@libs/chess/types';
import { cx } from '@libs/classnames';
import { BoardColorCircle } from '@ui/components/BoardColorCircle';
import { GameScore } from '@ui/components/GameScore/GameScore';

import styles from './GameView.module.css';

type GameViewProps = {
  className?: string;
  theme?: BoardThemes;
  tournamentId: string;
  gameId: string;
  fen?: string | null;
  whitePlayer: { userId: string; name: string; score: number };
  blackPlayer: { userId: string; name: string; score: number };
  orientation: 'Black' | 'White';
  onClick: (tournamentId: string, gameId: string) => void;
  result: 'NotStarted' | 'NotPlayed' | 'InProgress' | 'Unknown' | 'Draw' | 'WhiteWin' | 'BlackWin';
  renderQuickPeekWidget?: (userId: string) => ReactNode;
};
export function GameView({
  className,
  theme,
  tournamentId,
  gameId,
  fen,
  whitePlayer,
  blackPlayer,
  orientation,
  result,
  onClick,
  renderQuickPeekWidget,
}: GameViewProps) {
  const handleClick = useCallback(() => {
    onClick(gameId, tournamentId);
  }, [onClick, tournamentId, gameId]);

  const whitePlayerView = (
    <div
      className={cx(styles.player, styles.white, {
        [styles.winner]: result === 'WhiteWin',
        [styles.draw]: result === 'Draw',
      })}
    >
      <div className={styles.name}>
        <BoardColorCircle color="white" />
        {whitePlayer.name}
        {renderQuickPeekWidget?.(whitePlayer.userId)}
      </div>
      <div className={styles.score}>
        <GameScore score={whitePlayer.score} />
      </div>
    </div>
  );

  const blackPlayerView = (
    <div
      className={cx(styles.player, styles.black, {
        [styles.winner]: result === 'BlackWin',
        [styles.draw]: result === 'Draw',
      })}
    >
      <div className={styles.name}>
        <BoardColorCircle color="black" />
        {blackPlayer.name}
        {renderQuickPeekWidget?.(blackPlayer.userId)}
      </div>
      <div className={styles.score}>
        <GameScore score={blackPlayer.score} />
      </div>
    </div>
  );

  let winner: ChessWinner | undefined;
  if (result === 'WhiteWin') winner = 'white';
  if (result === 'BlackWin') winner = 'black';
  if (result === 'Draw') winner = 'draw';

  return (
    <div className={cx(styles.gameView, className)} onClick={handleClick}>
      {orientation === 'White' ? blackPlayerView : whitePlayerView}
      <ChessBoard
        className={styles.chessBoard}
        theme={theme}
        position={fen || undefined}
        orientation={orientation === 'Black' ? 'black' : 'white'}
        noShadow
        winner={winner}
        notation="none"
      />
      {orientation === 'White' ? whitePlayerView : blackPlayerView}
    </div>
  );
}
